interface Step {
  id: number;
  label: string;
  icon: string;
}

const DEFAULT_STEPS: Step[] = [
  { id: 1, label: "학습", icon: "ri-book-open-2-line" },
  { id: 2, label: "실습", icon: "ri-tools-line" },
  { id: 3, label: "제출", icon: "ri-upload-cloud-2-line" },
  { id: 4, label: "피드백", icon: "ri-sparkling-2-line" },
];

const ANALYZE_STEPS: Step[] = [
  { id: 1, label: "학습", icon: "ri-book-open-2-line" },
  { id: 2, label: "분석", icon: "ri-search-eye-line" },
  { id: 3, label: "실습", icon: "ri-tools-line" },
  { id: 4, label: "제출", icon: "ri-upload-cloud-2-line" },
  { id: 5, label: "피드백", icon: "ri-sparkling-2-line" },
];

interface Props {
  currentStep: number;
  completedSteps: number[];
  analyzeMode?: boolean;
}

export default function StepIndicator({ currentStep, completedSteps, analyzeMode }: Props) {
  const steps = analyzeMode ? ANALYZE_STEPS : DEFAULT_STEPS;

  return (
    <div className="bg-white rounded-2xl px-6 py-5 mb-6">
      {analyzeMode && (
        <div className="flex items-center gap-1.5 mb-3 pb-3 border-b border-[#F2F4F6]">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-red-500 to-orange-400 text-white">
            ACE 콤보
          </span>
          <span className="text-[10px] text-[#8B95A1]">Analyze → Create 연결 플로우</span>
        </div>
      )}
      <div className="flex items-center justify-between relative">
        {/* Connector line background */}
        <div className="absolute left-0 right-0 top-[20px] h-[2px] bg-[#E5E8EC] z-0" style={{ marginLeft: steps.length === 5 ? "18px" : "20px", marginRight: steps.length === 5 ? "18px" : "20px" }} />
        {/* Progress line */}
        <div
          className="absolute h-[2px] bg-[#00D873] transition-all duration-700 z-0"
          style={{
            left: steps.length === 5 ? "18px" : "20px",
            right: `calc(${100 - ((currentStep - 1) / (steps.length - 1)) * 100}% )`,
            maxWidth: `calc(100% - ${steps.length === 5 ? "36px" : "40px"})`,
            top: "20px",
          }}
        />

        {steps.map((step) => {
          const isDone = completedSteps.includes(step.id);
          const isActive = currentStep === step.id;
          const isAnalyzeStep = analyzeMode && step.id === 2;

          const circleBg = isDone
            ? isAnalyzeStep ? "#FF8C00" : "#00D873"
            : isActive
            ? isAnalyzeStep ? "#FF8C00" : "#00D873"
            : "#FFFFFF";

          const circleBorder = !isDone && !isActive ? "ring-2 ring-[#E5E8EC]" : "";

          const circleRing = isActive
            ? `ring-4 ring-offset-0 ${isAnalyzeStep ? "ring-orange-100" : "ring-[#00D873]/20"}`
            : circleBorder;

          const labelColor = isActive
            ? isAnalyzeStep ? "text-[#FF8C00]" : "text-[#00D873]"
            : isDone
            ? isAnalyzeStep ? "text-[#FF8C00]/70" : "text-[#00D873]"
            : "text-[#8B95A1]";

          // circle animation
          const circleAnimation = isDone
            ? "stepCheckIn 0.48s cubic-bezier(0.175, 0.885, 0.32, 1.275) both"
            : isActive
            ? isAnalyzeStep
              ? "stepGlowOrange 2s ease-in-out infinite"
              : "stepGlowGreen 2s ease-in-out infinite"
            : "none";

          return (
            <div key={step.id} className="flex flex-col items-center gap-2 relative z-10">
              <div
                className={`${steps.length === 5 ? "w-9 h-9" : "w-10 h-10"} rounded-full flex items-center justify-center transition-colors duration-300 ${circleRing}`}
                style={{ backgroundColor: circleBg, animation: circleAnimation }}
              >
                {isDone ? (
                  <i
                    className={`ri-check-line ${steps.length === 5 ? "text-base" : "text-lg"} text-white`}
                    style={{ animation: "stepIconIn 0.35s 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275) both" }}
                  />
                ) : (
                  <i
                    className={`${step.icon} ${steps.length === 5 ? "text-base" : "text-lg"}`}
                    style={{ color: isActive || isDone ? "#fff" : "#8B95A1" }}
                  />
                )}
              </div>
              <span
                className={`${steps.length === 5 ? "text-[10px]" : "text-xs"} font-bold whitespace-nowrap transition-all duration-300 ${labelColor} ${isActive ? "scale-105" : "scale-100"}`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
